import axios from "@/plugins/axios";
import utils from "@/stores/utils";

const state = {
    establishment: null,
    providers: [],
    providers_loading: false,
    providers_loading_errors: null,
    creating_order: false,
};

const mutations = {
    updateEstablishment(state, establishment) {
        state.establishment = establishment;
    },
    updateProviders(state, providers) {
        state.providers = providers;
    },
    updateProvidersLoading(state, loading) {
        state.providers_loading = loading;
    },
    updateProvidersLoadingErrors(state, errors) {
        state.providers_loading_errors = errors;
    },
    updateCreatingOrder(state, creating) {
        state.creating_order = creating;
    },
    toggleProduct(state, { providerIndex, productIndex }) {
        if (state.providers[providerIndex] && state.providers[providerIndex].products[productIndex]) {
            state.providers[providerIndex].products[productIndex].selected = !state.providers[providerIndex].products[productIndex].selected;
        }
    },
    updateProductQuantity(state, { providerIndex, productIndex, quantity }) {
        if (state.providers[providerIndex] && state.providers[providerIndex].products[productIndex]) {
            state.providers[providerIndex].products[productIndex].edited_quantity = quantity;
        }
    },
};

const actions = {
    async init({ commit, dispatch, state }, params) {
        commit("updateEstablishment", params.establishment);
        commit("updateProviders", []);
        await dispatch("fetchProductsToOrder");
    },

    async fetchProductsToOrder({ commit, state }) {
        if (!state.establishment) {
            return;
        }

        commit("updateProvidersLoading", true);
        commit("updateProvidersLoadingErrors", null);

        try {
            const response = await axios.get(`/api/establishments/${state.establishment.id}/products-to-order/`);
            const providers = response.data.providers || [];
            // Initialize selected and edited_quantity fields for each product
            // Only pre-select products that have a suggested quantity_to_order > 0
            providers.forEach(provider => {
                if (provider.products) {
                    provider.products.forEach(product => {
                        if (!Object.prototype.hasOwnProperty.call(product, 'selected')) {
                            // Only pre-select if quantity_to_order > 0
                            product.selected = (product.quantity_to_order && parseFloat(product.quantity_to_order) > 0);
                        }
                        if (!Object.prototype.hasOwnProperty.call(product, 'edited_quantity')) {
                            product.edited_quantity = null;
                        }
                    });
                }
            });
            commit("updateProviders", providers);
            return response.data;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateProvidersLoadingErrors", error);
            commit("updateProviders", []);
            throw error;
        } finally {
            commit("updateProvidersLoading", false);
        }
    },

    toggleProduct({ commit, dispatch, state }, { provider, product, selected }) {
        const providerIndex = state.providers.findIndex((p) => (p.provider || p.provider_id) === (provider.provider || provider.provider_id));
        if (providerIndex !== -1) {
            const productIndex = state.providers[providerIndex].products.findIndex((p) => (p.product || p.product_id) === (product.product || product.product_id));
            if (productIndex !== -1) {
                commit("toggleProduct", { providerIndex, productIndex });
                // Update total amount for provider
                dispatch("updateProviderTotal", { providerIndex });
            }
        }
    },

    toggleAllProducts({ commit, dispatch, state }, { provider, selected }) {
        const providerIndex = state.providers.findIndex((p) => (p.provider || p.provider_id) === (provider.provider || provider.provider_id));
        if (providerIndex !== -1) {
            state.providers[providerIndex].products.forEach((product, productIndex) => {
                product.selected = selected;
            });
            // Update total amount for provider
            dispatch("updateProviderTotal", { providerIndex });
        }
    },

    updateQuantity({ commit, dispatch, state }, { provider, product, quantity }) {
        const providerIndex = state.providers.findIndex((p) => (p.provider || p.provider_id) === (provider.provider || provider.provider_id));
        if (providerIndex !== -1) {
            const productIndex = state.providers[providerIndex].products.findIndex((p) => (p.product || p.product_id) === (product.product || product.product_id));
            if (productIndex !== -1) {
                const numQuantity = parseFloat(quantity);
                if (!isNaN(numQuantity) && numQuantity >= 0) {
                    commit("updateProductQuantity", { providerIndex, productIndex, quantity: numQuantity });
                    // Update total amount for provider
                    dispatch("updateProviderTotal", { providerIndex });
                }
            }
        }
    },

    increaseQuantity({ dispatch }, { provider, product }) {
        const currentQuantity = product.edited_quantity !== null ? product.edited_quantity : product.quantity_to_order;
        dispatch("updateQuantity", { provider, product, quantity: parseFloat(currentQuantity) + 1 });
    },

    decreaseQuantity({ dispatch }, { provider, product }) {
        const currentQuantity = product.edited_quantity !== null ? product.edited_quantity : product.quantity_to_order;
        const newQuantity = Math.max(0, parseFloat(currentQuantity) - 1);
        dispatch("updateQuantity", { provider, product, quantity: newQuantity });
    },

    updateProviderTotal({ state, commit }, { providerIndex }) {
        if (state.providers[providerIndex]) {
            const provider = state.providers[providerIndex];
            const total = provider.products
                .filter((p) => p.selected)
                .reduce((sum, product) => {
                    const quantity = product.edited_quantity !== null ? product.edited_quantity : product.quantity_to_order;
                    const price = product.unit_purchase_price_excl_vat || 0;
                    return sum + parseFloat(quantity) * parseFloat(price);
                }, 0);
            // Use Vue.set to ensure reactivity
            state.providers[providerIndex].total_amount_excl_vat = total;
        }
    },

    async createOrderForProvider({ commit, state }, { provider, stockId }) {
        commit("updateCreatingOrder", true);
        try {
            // Get selected products for this provider with quantity > 0 and catalogentry
            const selectedItems = (provider.products || [])
                .filter(p => p.selected)
                .map(p => {
                    const quantity = p.edited_quantity !== null ? p.edited_quantity : p.quantity_to_order;
                    return {
                        catalogentry: p.catalogentry || p.catalogentry_id,
                        quantity: parseFloat(quantity) || 0,
                    };
                })
                .filter(item => item.quantity > 0 && item.catalogentry);

            if (selectedItems.length === 0) {
                throw new Error("No products selected");
            }

            // Get deliverysite from stock
            // Find deliverysite linked to this stock
            let deliverysiteId = null;
            try {
                const deliverysitesResponse = await axios.get('/api/deliverysites/', {
                    params: { stock: stockId }
                });
                const deliverysites = deliverysitesResponse.data?.results || deliverysitesResponse.data || [];
                if (deliverysites.length > 0) {
                    // Find one explicitly linked to the stock
                    const stockDeliverysite = deliverysites.find(ds => ds.stock === stockId || ds.stock_data?.id === stockId);
                    deliverysiteId = stockDeliverysite ? stockDeliverysite.id : deliverysites[0].id;
                }
            } catch (error) {
                console.warn('Could not find deliverysite for stock:', error);
            }

            if (!deliverysiteId) {
                throw new Error("No delivery site found for this warehouse. Please create a delivery site linked to this warehouse first.");
            }

            // Build payload for provider order creation
            const providerId = provider.provider || provider.provider_id;
            const payload = {
                deliverysite: deliverysiteId,
                items: selectedItems.map(item => ({
                    catalogentry: item.catalogentry,
                    quantity: item.quantity,
                })),
            };

            const response = await axios.post(`/api/providers/${providerId}/create-order/`, payload);
            return response.data;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            throw error;
        } finally {
            commit("updateCreatingOrder", false);
        }
    },

    async createMaintenanceForProvider({ commit, state }, provider) {
        commit("updateCreatingOrder", true);
        try {
            const selectedProducts = provider.products.filter((p) => p.selected).map((p) => ({
                product_id: p.product_id,
                quantity: p.edited_quantity !== null ? p.edited_quantity : p.quantity_to_order,
            }));

            // TODO: Implement API call to create maintenance
            // const response = await axios.post(`/api/maintenances/`, {
            //     provider: provider.provider_id,
            //     establishment: state.establishment.id,
            //     items: selectedProducts,
            // });

            console.log("Create maintenance for provider:", provider.provider_id, "with products:", selectedProducts);
            
            // For now, just show an alert
            alert("Maintenance creation will be implemented");
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            throw error;
        } finally {
            commit("updateCreatingOrder", false);
        }
    },
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
};

